import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Bar,
    Line,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { formatNumber } from '../produksi';
import { formatPeriode } from '../EstateDashboardComponent';
import { CustomTooltipTon } from '../CustomTooltip';

const COLORS = {
    Produksi: '#82CA9D',
    KER: '#8884D8',
    Stok: '#CA6060',
    Kirim: '#FFC658',
};

export interface RawDatum {
    periode: string;
    descr: '% KER' | 'Kirim' | 'Stok' | 'Produksi' | 'Jam Olah';
    qty: number;
    qty_stok_yearly?: number; // optional for yearly stock
}

interface KernelTrendProps {
    data: RawDatum[];
}

const KernelTrend: React.FC<KernelTrendProps> = ({ data }) => {
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);

    let stokOpacity = 1;
    let produksiOpacity = 1;
    let kirimOpacity = 1;
    let kerOpacity = 1;

    if (hoveringDataKey === 'Stok') {
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
        kerOpacity = 0.5;
    } else if (hoveringDataKey === 'Produksi') {
        stokOpacity = 0.5;
        kirimOpacity = 0.5;
        kerOpacity = 0.5;
    } else if (hoveringDataKey === 'Kirim') {
        stokOpacity = 0.5;
        produksiOpacity = 0.5;
        kerOpacity = 0.5;
    } else if (hoveringDataKey === 'KER') {
        stokOpacity = 0.5;
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
    }

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };
    // pivot raw API → wide format per periode
    const formattedData = useMemo(() => {
        const tmp: Record<string, any> = {};
        data.forEach(({ periode, descr, qty }) => {
            if (!tmp[periode]) {
                tmp[periode] = { periode, Kirim: 0, Stok: 0, Produksi: 0, KER: 0 };
            }
            // simpan ke key yang sesuai
            tmp[periode][descr === '% KER' ? 'KER' : descr] = qty;
        });
            return Object.values(tmp).map(item => ({
            ...item,
            Kirim: item.Kirim / 1000,
            Stok: item.Stok / 1000,
            Produksi: item.Produksi / 1000,
            // KER tetap apa adanya
        }));
    }, [data]);

    const year = data[0]?.periode?.slice(0, 4) || new Date().getFullYear().toString();
    return (
        <Box p={4} mb={4} height="100%" w="100%">
            <Text fontSize="xl" fontWeight="semibold" mb={4}>
                Produksi Kernel Bulanan {year}
            </Text>
            <Box 
                // flex={1} 
                minH="130px" 
                height="350px"
                // minWidth="690px"
                // overflowX="auto" 
                // overflowY="hidden"
                w="100%"
            >
                <ResponsiveContainer width="100%" height={380}>
                    <ComposedChart data={formattedData} barGap={0}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="periode" tickFormatter={formatPeriode} interval={0} height={60} tick={{ fontSize: 12 }}/>
                        {/* YAxis kiri untuk ton */}
                        <YAxis
                            yAxisId="left"
                            tickFormatter={v => formatNumber(v, 'number')}
                            width={80}
                            tick={{ fontSize: 12 }}
                            domain={[0, 'auto']}
                            label={{ value: 'Ton', angle: -90, position: 'insideLeft', offset: 5 }}
                        />
                        {/* YAxis kanan untuk persen */}
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            tickFormatter={v => `${v}%`}
                            domain={[0, 'auto']}
                        />
                        <Tooltip content={<CustomTooltipTon />} />
                        <Legend verticalAlign="bottom" height={60} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />

                        {/* Bars */}
                        <Bar yAxisId="left" name="Produksi" dataKey="Produksi" stackId="a" fill={COLORS.Produksi} fillOpacity={produksiOpacity} />
                        <Bar yAxisId="left" name="Kirim" dataKey="Kirim" fill={COLORS.Kirim} fillOpacity={kirimOpacity} />

                        <Line
                            yAxisId="right"
                            name="KER"
                            type="linear"
                            dataKey="KER"
                            stroke={COLORS.KER}
                            strokeWidth={2}
                            strokeOpacity={kerOpacity}
                            dot={{ r: 7 }}
                            activeDot={{ r: 7 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default KernelTrend;